/**
 * useWechat Hook
 *
 * Manages the WeChat gateway service within the Ink interactive UI.
 * Listens for WeChat message events and translates them into UI state
 * so the App can render incoming/outgoing messages, tool calls, and streaming.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { WechatGatewayService, WechatGatewayEvent } from '../../wechat/service.js';
import type { ToolCallRecord } from '../components/ToolCallIndicator.js';

export interface WechatRemoteRequest {
    sender: string;
    text: string;
}

export interface CompletedWechatExchange {
    userMessage: string;
    assistantContent: string;
    sender: string;
}

export interface UseWechatOptions {
    service: WechatGatewayService | null;
}

export interface UseWechatReturn {
    isOnline: boolean;
    isProcessingRemote: boolean;
    remoteRequest: WechatRemoteRequest | null;
    remoteStreamingText: string;
    remoteToolCallHistory: ToolCallRecord[];
    remoteError: string | null;
    completedCount: number;
    lastCompletedExchange: CompletedWechatExchange | null;
    clearCompletedExchange: () => void;
}

export function useWechat(options: UseWechatOptions): UseWechatReturn {
    const { service } = options;

    const [isOnline, setIsOnline] = useState(false);
    const [isProcessingRemote, setIsProcessingRemote] = useState(false);
    const [remoteRequest, setRemoteRequest] = useState<WechatRemoteRequest | null>(null);
    const [remoteStreamingText, setRemoteStreamingText] = useState('');
    const [remoteToolCallHistory, setRemoteToolCallHistory] = useState<ToolCallRecord[]>([]);
    const [remoteError, setRemoteError] = useState<string | null>(null);
    const [completedCount, setCompletedCount] = useState(0);
    const [lastCompletedExchange, setLastCompletedExchange] = useState<CompletedWechatExchange | null>(null);

    const streamingTextRef = useRef('');
    const activeRequestRef = useRef<WechatRemoteRequest | null>(null);

    const trackToolCall = useCallback((id: string, name: string, type: 'tool' | 'mcp', status: 'pending' | 'completed' | 'error', serverName?: string) => {
        setRemoteToolCallHistory(prev => {
            const existing = prev.findIndex(t => t.id === id);
            if (existing >= 0) {
                const updated = [...prev];
                updated[existing] = { ...updated[existing], status };
                return updated;
            }
            return [...prev, { id, name, type, serverName, status, timestamp: Date.now() }];
        });
    }, []);

    useEffect(() => {
        if (!service) return;

        const handleEvent = (event: WechatGatewayEvent) => {
            switch (event.type) {
                case 'started':
                    setIsOnline(true);
                    break;

                case 'stopped':
                    setIsOnline(false);
                    setIsProcessingRemote(false);
                    setRemoteRequest(null);
                    break;

                case 'processing_start': {
                    const req = { sender: event.sender, text: event.text };
                    setIsProcessingRemote(true);
                    setRemoteRequest(req);
                    activeRequestRef.current = req;
                    setRemoteStreamingText('');
                    setRemoteToolCallHistory([]);
                    setRemoteError(null);
                    streamingTextRef.current = '';
                    break;
                }

                case 'remote_content':
                    if (event.partial) {
                        streamingTextRef.current += event.text;
                    } else {
                        streamingTextRef.current = event.text;
                    }
                    setRemoteStreamingText(streamingTextRef.current);
                    break;

                case 'remote_tool_call':
                    trackToolCall(event.id, event.name, event.isMcp ? 'mcp' : 'tool', 'pending', event.serverName);
                    break;

                case 'remote_tool_result':
                    trackToolCall(event.id, event.name, 'tool', event.status, undefined);
                    break;

                case 'remote_error':
                    setRemoteError(event.error);
                    break;

                case 'processing_done': {
                    const finishedReq = activeRequestRef.current;
                    if (finishedReq && streamingTextRef.current) {
                        setLastCompletedExchange({
                            userMessage: finishedReq.text,
                            assistantContent: streamingTextRef.current,
                            sender: finishedReq.sender,
                        });
                    }
                    setCompletedCount(prev => prev + 1);
                    setIsProcessingRemote(false);
                    setRemoteRequest(null);
                    setRemoteStreamingText('');
                    setRemoteToolCallHistory([]);
                    activeRequestRef.current = null;
                    streamingTextRef.current = '';
                    break;
                }

                case 'error':
                    setRemoteError(event.error);
                    break;
            }
        };

        service.on('event', handleEvent);

        return () => {
            service.removeListener('event', handleEvent);
        };
    }, [service, trackToolCall]);

    const clearCompletedExchange = useCallback(() => {
        setLastCompletedExchange(null);
    }, []);

    return {
        isOnline,
        isProcessingRemote,
        remoteRequest,
        remoteStreamingText,
        remoteToolCallHistory,
        remoteError,
        completedCount,
        lastCompletedExchange,
        clearCompletedExchange,
    };
}
